import {callAPI} from '@/store/utils'; // eslint-disable-line
import {base} from './utils';

// Ledger entries & review workflow (§8 of the API contract, TT-7122 — updated,
// breaking). The list endpoint is filtered server-side and paginated:
//   GET /entries?status=&exported=&journal_id=&journal_type=&date_from=&date_to=
//     &amount_min=&amount_max=&q=&page=&page_size=
// → result: { items: [headers], total, page, page_size }.
// `status` is comma-separated (draft,validated,excluded,reversed); `exported`
// splits validated (pushed vs to-export); `q` matches reference OR a line
// label. The detail (with lines) stays on GET /entries/<id>. Composed into the
// `accounting` namespace by ./index.js.
const QUERY_KEYS = {
  status: 'status',
  exported: 'exported',
  journalId: 'journal_id',
  journalType: 'journal_type',
  dateFrom: 'date_from',
  dateTo: 'date_to',
  amountMin: 'amount_from',
  amountMax: 'amount_to',
  q: 'q',
  page: 'page',
  pageSize: 'page_size',
};

function buildQuery(params) {
  const usp = new URLSearchParams();
  Object.entries(QUERY_KEYS).forEach(([camel, snake]) => {
    const v = params[camel];
    // Skip empty/null; keep booleans (exported=false is meaningful).
    if (v === undefined || v === null || v === '') return;
    usp.set(snake, typeof v === 'boolean' ? String(v) : v);
  });
  return usp.toString();
}

export default {
  state: {
    entries: [],
    entriesTotal: 0,
    entriesPage: 1,
    entriesPageSize: 50,
    entriesLoaded: false,
    // entry id -> { entry, lines }
    entryDetails: {},
  },
  getters: {
    entries: (state) => state.entries,
    entriesTotal: (state) => state.entriesTotal,
    entriesPage: (state) => state.entriesPage,
    entriesPageSize: (state) => state.entriesPageSize,
    entriesLoaded: (state) => state.entriesLoaded,
    entryDetail: (state) => (id) => state.entryDetails[id] || null,
  },
  mutations: {
    setEntriesResult(state, result) {
      state.entries = result?.items || [];
      state.entriesTotal = result?.total || 0;
      state.entriesPage = result?.page || 1;
      state.entriesPageSize = result?.page_size || 50;
      state.entriesLoaded = true;
    },
    // Infinite scroll: append the next page to the current list.
    appendEntriesResult(state, result) {
      state.entries = [...state.entries, ...(result?.items || [])];
      if (result?.total != null) state.entriesTotal = result.total;
      state.entriesPage = result?.page || state.entriesPage;
      state.entriesPageSize = result?.page_size || state.entriesPageSize;
    },
    setEntryDetail(state, {id, entry, lines}) {
      state.entryDetails = {
        ...state.entryDetails,
        [id]: {entry: entry || null, lines: lines || []},
      };
    },
  },
  actions: {
    async actionGetEntries({commit}, {append = false, ...params} = {}) {
      const qs = buildQuery(params);
      const res = await callAPI('GET', `${base()}/entries?${qs}`);
      if (res && !res.error) {
        commit(append ? 'appendEntriesResult' : 'setEntriesResult', res.result);
      }
      return res;
    },
    async actionGetEntry({commit}, {id}) {
      const res = await callAPI('GET', `${base()}/entries/${id}`);
      if (res && !res.error) {
        commit('setEntryDetail', {
          id,
          entry: res.result?.entry,
          lines: res.result?.lines,
        });
      }
      return res;
    },
    // Replace the lines of a draft (PATCH). Refreshes the detail; the screen
    // reloads the current query. lines: [{account_id, debit, credit,
    // counterparty_id?, label?}].
    async actionUpdateEntryLines({dispatch}, {id, lines}) {
      const res = await callAPI('PATCH', `${base()}/entries/${id}`, {lines});
      if (res && !res.error) await dispatch('actionGetEntry', {id});
      return res;
    },
    // Bulk lifecycle action over a selection model (shared with transactions):
    // action ∈ validate | exclude | reinstate | mark-exported. body:
    // {selectedIds?, selectedAll?, ignoreIds?, filters:{…same keys as listing}}.
    // Result: {selected, processed, skipped, errors}. The screen refreshes after.
    async actionBulkEntryAction(_ctx, {action, selection}) {
      return callAPI('POST', `${base()}/entries/bulk-${action}`, selection);
    },
    // Generate an export file (FEC/CSV) for the entries matching the filter —
    // POST /entries/export. Returns the raw Response (immediate) so the caller
    // can download the binary body; the backend marks the exported entries.
    async actionExportEntries(_ctx, params = {}) {
      const body = {format: params.format, status: params.status};
      if (params.exported !== undefined) body.exported = params.exported;
      if (params.journalId) body.journal_id = params.journalId;
      if (params.fiscalYearId) body.fiscal_year_id = params.fiscalYearId;
      if (params.dateFrom) body.date_from = params.dateFrom;
      if (params.dateTo) body.date_to = params.dateTo;
      // immediate=true → raw Response (binary file, or JSON on error).
      return callAPI('POST', `${base()}/entries/export`, body, false, 60, 0, true);
    },
    // Lifecycle transition: action ∈ validate | exclude | reinstate | reverse |
    // export. Refreshes the entry detail; the screen reloads the current query.
    async actionEntryAction({dispatch}, {id, action}) {
      const res = await callAPI('POST', `${base()}/entries/${id}/${action}`);
      if (res && !res.error) await dispatch('actionGetEntry', {id});
      return res;
    },
  },
};
